import { useEffect, useState } from "react";
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Link,
    Tooltip,
    CircularProgress,
    styled,
} from "@mui/material";
import { tableCellClasses } from "@mui/material/TableCell";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import deleteIcon from "../../../../../../../assets/images/delete-dustbin.svg";
import { useAuthUser } from "react-auth-kit";
import { useDispatch, useSelector } from "react-redux";
import {
    getEnquiryTags,
    createEnquiryTag,
    deleteEnquiryTag,
} from "../../../../service/settingsSlice";
import styles from "./TagCustomizationTab.module.css";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        fontSize: "14px",
        color: "#fff",
        background: "#073E53",
        textTransform: "capitalize",
        fontWeight: "400",
    },
    [`&.${tableCellClasses.body}`]: {
        fontSize: 12,
    },
}));

interface Tag {
    id: string;
    tagName: string;
    status: "ACTIVE" | "INACTIVE";
    isSystemTag?: boolean;
    tagType?: string;
    leadsCount: number;
    canDelete?: boolean;
}

function TagCustomizationTab() {
    const auth = useAuthUser();
    const authUser = auth();
    const dispatch = useDispatch<any>();

    const instituteFromLocalStorage = JSON.parse(
        localStorage.getItem("ACTIVE_INSTITUTE")!
    );
    const instituteId = instituteFromLocalStorage?.id;

    const {
        enquiryTags,
        enquiryTagsLoading,
        createEnquiryTagLoading,
        createEnquiryTagSuccess,
        deleteEnquiryTagSuccess,
    } = useSelector((state: any) => state.appSettings);

    const [searchTerm, setSearchTerm] = useState<string>("");
    const [editingTagId, setEditingTagId] = useState<string | null>(null);
    const [editingTagName, setEditingTagName] = useState<string>("");
    const [editConfirmDialogOpen, setEditConfirmDialogOpen] = useState<boolean>(false);
    const [isAddingNewTag, setIsAddingNewTag] = useState<boolean>(false);
    const [newTagName, setNewTagName] = useState<string>("");
    const [selectedTag, setSelectedTag] = useState<Tag | null>(null);
    const [pendingEditTagId, setPendingEditTagId] = useState<string | null>(null);
    const [deleteConfirmDialogOpen, setDeleteConfirmDialogOpen] = useState<boolean>(false);
    const [tagToDelete, setTagToDelete] = useState<Tag | null>(null);

    const systemTags: Tag[] = Array.isArray(enquiryTags?.systemTags)
        ? enquiryTags.systemTags
        : [];
    const customTags: Tag[] = Array.isArray(enquiryTags?.customTags)
        ? enquiryTags.customTags
        : [];
    const visibleSystemTags = systemTags.filter(
        (tag) => tag.tagName?.toLowerCase() !== "select"
    );
    const tags: Tag[] = [...visibleSystemTags, ...customTags];
    const filteredSystemTags = systemTags.filter(
        (tag) =>
            tag.tagName?.toLowerCase() !== "select" &&
            tag.tagName?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    const filteredCustomTags = customTags.filter((tag) =>
        tag.tagName?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    useEffect(() => {
        if (instituteId) {
            dispatch(getEnquiryTags(instituteId));
        }
    }, [instituteId]);

    useEffect(() => {
        if (createEnquiryTagSuccess && instituteId) {
            dispatch(getEnquiryTags(instituteId));
            setIsAddingNewTag(false);
            setNewTagName("");
        }
    }, [createEnquiryTagSuccess]);

    useEffect(() => {
        if (deleteEnquiryTagSuccess && instituteId) {
            dispatch(getEnquiryTags(instituteId));
        }
    }, [deleteEnquiryTagSuccess]);

    const handleEditClick = (tag: Tag) => {
        setEditingTagId(tag.id);
        setEditingTagName(tag.tagName);
    };

    const handleEditCancel = () => {
        setEditingTagId(null);
        setEditingTagName("");
    };

    const handleEditConfirm = (tagId: string) => {
        if (editingTagName.trim()) {
            const tag = tags.find((t) => t.id === tagId);
            if (tag && tag.tagName !== editingTagName.trim()) {
                setPendingEditTagId(tagId);
                setSelectedTag(tag);
                setEditConfirmDialogOpen(true);
            } else {
                setEditingTagId(null);
                setEditingTagName("");
            }
        }
    };

    const handleEditConfirmProceed = () => {
        if (pendingEditTagId && editingTagName.trim()) {
            setEditingTagId(null);
            setEditingTagName("");
            setEditConfirmDialogOpen(false);
            setPendingEditTagId(null);
            setSelectedTag(null);
        }
    };

    const handleEditConfirmCancel = () => {
        setEditConfirmDialogOpen(false);
        setPendingEditTagId(null);
        setSelectedTag(null);
        setEditingTagId(null);
        setEditingTagName("");
    };

    const handleAddClick = () => {
        setIsAddingNewTag(true);
        setNewTagName("");
    };

    const handleAddCancel = () => {
        setIsAddingNewTag(false);
        setNewTagName("");
    };

    const handleAddConfirm = () => {
        if (newTagName.trim() && instituteId) {
            dispatch(
                createEnquiryTag({
                    tagName: newTagName.trim(),
                    instituteId: instituteId,
                    createdById: authUser?.institute?.[0]?.id,
                    createdByName: authUser?.firstName + " " + authUser?.lastName,
                })
            );
        }
    };

    const handleDeleteTag = (tag: Tag) => {
        setTagToDelete(tag);
        setDeleteConfirmDialogOpen(true);
    };

    const handleDeleteConfirmProceed = () => {
        if (tagToDelete && instituteId) {
            dispatch(
                deleteEnquiryTag({
                    id: tagToDelete.id,
                    instituteId: instituteId,
                    updatedById: authUser?.institute?.[0]?.id,
                    updatedByName: authUser?.firstName + " " + authUser?.lastName,
                })
            );
        }
        setDeleteConfirmDialogOpen(false);
        setTagToDelete(null);
    };

    const handleDeleteConfirmCancel = () => {
        setDeleteConfirmDialogOpen(false);
        setTagToDelete(null);
    };

    const handleLeadsClick = (tag: Tag) => {
        console.log(`Navigating to leads with tag: ${tag.tagName}`);
    };

    const getStatusBadgeClass = (status: string) => {
        switch (status) {
            case "ACTIVE":
                return styles.statusActive;
            case "INACTIVE":
                return styles.statusInactive;
            default:
                return styles.statusActive;
        }
    };

    const renderSystemTagRow = (tag: Tag) => (
        <TableRow key={tag.id} className={styles.tableRow}>
            <TableCell className={styles.tableCell}>
                {editingTagId === tag.id ? (
                    <div className={styles.editingWrapper}>
                        <TextField
                            value={editingTagName}
                            onChange={(e) => setEditingTagName(e.target.value)}
                            size="small"
                            autoFocus
                            className={styles.editTextField}
                            inputProps={{
                                style: {
                                    padding: "6px 8px",
                                    fontSize: "14px",
                                },
                            }}
                        />
                        <IconButton
                            size="small"
                            onClick={() => handleEditConfirm(tag.id)}
                            className={styles.editActionButton}
                            disabled={!editingTagName.trim()}
                        >
                            <CheckIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                            size="small"
                            onClick={handleEditCancel}
                            className={styles.editActionButton}
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </div>
                ) : (
                    <span>{tag.tagName}</span>
                )}
            </TableCell>
        </TableRow>
    );

    const renderCustomTagRow = (tag: Tag) => (
        <TableRow key={tag.id} className={styles.tableRow}>
            <TableCell className={styles.tableCell}>
                {editingTagId === tag.id ? (
                    <div className={styles.editingWrapper}>
                        <TextField
                            value={editingTagName}
                            onChange={(e) => setEditingTagName(e.target.value)}
                            size="small"
                            autoFocus
                            className={styles.editTextField}
                            inputProps={{
                                style: {
                                    padding: "6px 8px",
                                    fontSize: "14px",
                                },
                            }}
                        />
                        <IconButton
                            size="small"
                            onClick={() => handleEditConfirm(tag.id)}
                            className={styles.editActionButton}
                            disabled={!editingTagName.trim()}
                        >
                            <CheckIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                            size="small"
                            onClick={handleEditCancel}
                            className={styles.editActionButton}
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </div>
                ) : (
                    <span>{tag.tagName}</span>
                )}
            </TableCell>
            <TableCell className={styles.tableCell}>
                <Link
                    component="button"
                    variant="body2"
                    onClick={() => handleLeadsClick(tag)}
                    className={styles.leadsLink}
                    underline="hover"
                >
                    {tag.leadsCount || 0}
                </Link>
            </TableCell>
            <TableCell className={styles.tableCell} align="right">
                <Tooltip
                    title={
                        tag.leadsCount > 0 && tag.canDelete === false
                            ? "Cannot delete tag with associated leads"
                            : "Delete tag"
                    }
                    arrow
                >
                    <span>
                        <IconButton
                            size="small"
                            onClick={() => handleDeleteTag(tag)}
                            className={styles.deleteButton}
                            disabled={tag.leadsCount > 0 && tag.canDelete === false}
                            sx={{ color: "#d32f2f", "&.Mui-disabled": { color: "#d32f2f", opacity: 0.5 } }}
                        >
                            <img src={deleteIcon} alt="delete" style={{ width: 18, height: 18 }} />
                        </IconButton>
                    </span>
                </Tooltip>
            </TableCell>
        </TableRow>
    );

    const renderSystemTagTable = (tagList: Tag[]) => (
        <TableContainer className={`${styles.tableContainer} ${styles.scrollableTable}`}>
            <Table stickyHeader>
                <TableHead>
                    <TableRow>
                        <StyledTableCell sx={{ width: "100%" }}>Tag Name</StyledTableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {tagList.length > 0 ? (
                        tagList.map((tag) => renderSystemTagRow(tag))
                    ) : (
                        <TableRow>
                            <TableCell colSpan={1} className={styles.noDataCell}>
                                No tags found
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
        </TableContainer>
    );

    const renderCustomTagTable = (tagList: Tag[]) => (
        <TableContainer className={`${styles.tableContainer} ${styles.scrollableTable}`}>
            <Table stickyHeader>
                <TableHead>
                    <TableRow>
                        <StyledTableCell sx={{ width: "40%" }}>Tag Name</StyledTableCell>
                        <StyledTableCell sx={{ width: "30%" }}>Leads</StyledTableCell>
                        <StyledTableCell sx={{ width: "30%" }} align="right">
                            Action
                        </StyledTableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {tagList.length > 0 ? (
                        tagList.map((tag) => renderCustomTagRow(tag))
                    ) : (
                        <TableRow>
                            <TableCell colSpan={3} className={styles.noDataCell}>
                                No custom tags found
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
        </TableContainer>
    );

    if (enquiryTagsLoading) {
        return (
            <div className={styles.tagCustomizationWrap} style={{ alignItems: "center", justifyContent: "center" }}>
                <CircularProgress sx={{ color: "#11C89B" }} />
            </div>
        );
    }

    return (
        <div className={styles.tagCustomizationWrap}>
            <div className={styles.tagHeader}>
                <div className={styles.tagHeaderLeft}>
                    <div className={styles.tagTitleRow}>
                        <div className={styles.tagTitle}>Tag Customize</div>
                        <div className={styles.tagCountBadge}>
                            <LocalOfferIcon className={styles.tagIcon} />
                            <span>{tags.length} Total</span>
                        </div>
                    </div>
                </div>
                <div className={styles.headerActions}>
                    {/* <TextField
                        placeholder="Search tags"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        inputProps={{
                            style: {
                                background: "#ffffff",
                                padding: "8px 12px",
                                width: "150px",
                            },
                        }}
                        type="search"
                    /> */}
                    {isAddingNewTag ? (
                        <div className={styles.inlineAddWrapper}>
                            <TextField
                                value={newTagName}
                                onChange={(e) => setNewTagName(e.target.value)}
                                size="small"
                                autoFocus
                                placeholder="Enter new tag name"
                                className={styles.inlineAddTextField}
                                inputProps={{
                                    style: {
                                        padding: "8px 12px",
                                        fontSize: "14px",
                                    },
                                }}
                            />
                            <Button
                                variant="contained"
                                onClick={handleAddConfirm}
                                disabled={!newTagName.trim() || createEnquiryTagLoading}
                                sx={{
                                    backgroundColor: "#11C89B",
                                    textTransform: "none",
                                    minWidth: "70px",
                                    "&:hover": { backgroundColor: "#0FA885" },
                                }}
                            >
                                {createEnquiryTagLoading ? (
                                    <CircularProgress size={20} sx={{ color: "#fff" }} />
                                ) : (
                                    "Save"
                                )}
                            </Button>
                            <Button
                                variant="outlined"
                                onClick={handleAddCancel}
                                sx={{
                                    textTransform: "none",
                                    minWidth: "70px",
                                    borderColor: "#d0d5dd",
                                    color: "#667085",
                                    "&:hover": {
                                        borderColor: "#b0b5bd",
                                        backgroundColor: "#f9fafb",
                                    },
                                }}
                            >
                                Cancel
                            </Button>
                        </div>
                    ) : (
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={handleAddClick}
                            sx={{
                                backgroundColor: "#11C89B",
                                textTransform: "none",
                                "&:hover": { backgroundColor: "#0FA885" },
                            }}
                        >
                            Add Custom Tag
                        </Button>
                    )}
                </div>
            </div>

            <div className={styles.tablesWrapper}>
                <div className={styles.categorySection}>
                    <div className={styles.categoryTitle}>System Tags</div>
                    {renderSystemTagTable(filteredSystemTags)}
                </div>

                <div className={styles.categorySection}>
                    <div className={styles.categoryTitle}>Custom Tags</div>
                    {renderCustomTagTable(filteredCustomTags)}
                </div>
            </div>

            {/* Edit Confirmation Dialog */}
            <Dialog open={editConfirmDialogOpen} onClose={handleEditConfirmCancel} maxWidth="sm" fullWidth>
                <DialogTitle className={styles.dialogTitle}>
                    <div className={styles.dialogTitleContent}>
                        <span>Confirm Tag Name Change</span>
                        <IconButton
                            onClick={handleEditConfirmCancel}
                            className={styles.dialogCloseButton}
                            size="small"
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </div>
                </DialogTitle>
                <DialogContent>
                    <div className={styles.deleteMessage}>
                        This change cannot be undone. All connected leads with the tag "{selectedTag?.tagName}" will be updated to "{editingTagName}".
                        <div style={{ marginTop: '16px' }}>
                            Are you sure you want to proceed with this change?
                        </div>
                    </div>
                </DialogContent>
                <DialogActions className={styles.dialogActions}>
                    <Button
                        onClick={handleEditConfirmCancel}
                        variant="outlined"
                        fullWidth
                        sx={{
                            textTransform: "none",
                            borderColor: "#d0d5dd",
                            color: "#667085",
                            padding: "10px 16px",
                            "&:hover": {
                                borderColor: "#b0b5bd",
                                backgroundColor: "#f9fafb",
                            },
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleEditConfirmProceed}
                        variant="contained"
                        fullWidth
                        sx={{
                            textTransform: "none",
                            backgroundColor: "#11C89B",
                            padding: "10px 16px",
                            "&:hover": {
                                backgroundColor: "#0FA885",
                            },
                        }}
                    >
                        Confirm
                    </Button>
                </DialogActions>
            </Dialog>
            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteConfirmDialogOpen}
                onClose={handleDeleteConfirmCancel}
                maxWidth="xs"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: "12px",
                        overflow: "hidden",
                    },
                }}
            >
                <div className={styles.deleteDialogHeader}>
                    <IconButton
                        onClick={handleDeleteConfirmCancel}
                        className={styles.dialogCloseButton}
                        size="small"
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </div>
                <DialogContent className={styles.deleteDialogContent}>
                    <div className={styles.deleteIconWrapper}>
                        <img src={deleteIcon} alt="delete" className={styles.deleteDialogIcon} />
                    </div>
                    <div className={styles.deleteDialogTitle}>Delete Tag</div>
                    <div className={styles.deleteDialogMessage}>
                        Are you sure you want to delete{" "}
                        <span className={styles.deleteTagName}>
                            {tagToDelete?.tagName}
                        </span>
                        ? This action cannot be undone.
                    </div>
                </DialogContent>
                <DialogActions className={styles.deleteDialogActions}>
                    <Button
                        onClick={handleDeleteConfirmCancel}
                        variant="outlined"
                        fullWidth
                        sx={{
                            textTransform: "none",
                            borderColor: "#d0d5dd",
                            color: "#344054",
                            padding: "10px 16px",
                            borderRadius: "8px",
                            fontWeight: 500,
                            "&:hover": {
                                borderColor: "#b0b5bd",
                                backgroundColor: "#f9fafb",
                            },
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleDeleteConfirmProceed}
                        variant="contained"
                        fullWidth
                        sx={{
                            textTransform: "none",
                            backgroundColor: "#d32f2f",
                            padding: "10px 16px",
                            borderRadius: "8px",
                            fontWeight: 500,
                            "&:hover": {
                                backgroundColor: "#b71c1c",
                            },
                        }}
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
}

export default TagCustomizationTab;
